import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ADMIN_LOCALE_OPTIONS, type AdminLocale } from '../i18n';
import { useAdminLocale } from '../i18n/LocaleProvider';

function Flag({ code, emoji }: { code: string; emoji: string }) {
  return (
    <span
      aria-hidden
      style={{
        width: 22,
        height: 16,
        borderRadius: 3,
        overflow: 'hidden',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.08)',
        background: '#f3f4f6',
        fontSize: 16,
        lineHeight: 1,
      }}
    >
      <img
        src={`https://flagcdn.com/w40/${code}.png`}
        alt=""
        width={22}
        height={16}
        style={{ width: 22, height: 16, objectFit: 'cover', display: 'block' }}
        onError={(e) => {
          const img = e.currentTarget;
          img.style.display = 'none';
          const parent = img.parentElement;
          if (parent && !parent.dataset.flagFallback) {
            parent.dataset.flagFallback = '1';
            parent.textContent = emoji;
          }
        }}
      />
    </span>
  );
}

export function AdminLanguageMenu() {
  const { locale, setLocale } = useAdminLocale();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const current =
    ADMIN_LOCALE_OPTIONS.find((o) => o.value === locale) ||
    ADMIN_LOCALE_OPTIONS[0];

  const place = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const width = menuRef.current?.offsetWidth || 260;
    let left = r.left;
    if (left + width > window.innerWidth - 8) {
      left = r.right - width;
    }
    if (left < 8) left = 8;
    setPos({ top: r.bottom + 8, left });
  };

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const id = requestAnimationFrame(place);
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onReposition = () => place();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  function pick(next: AdminLocale) {
    setOpen(false);
    setLocale(next);
  }

  const menu =
    open &&
    createPortal(
      <div
        ref={menuRef}
        role="listbox"
        aria-label="Language"
        style={{
          position: 'fixed',
          top: pos.top,
          left: pos.left,
          zIndex: 100000,
          minWidth: 220,
          maxHeight: 'min(420px, calc(100vh - 24px))',
          overflowY: 'auto',
          padding: '10px 8px',
          borderRadius: 22,
          background: '#fff',
          boxShadow:
            '0 12px 32px rgba(16,24,40,.14), 0 0 0 1px rgba(16,24,40,.06)',
        }}
      >
        {ADMIN_LOCALE_OPTIONS.map((opt) => {
          const selected = opt.value === locale;
          return (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={selected}
              onClick={() => pick(opt.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                padding: '10px 12px',
                border: 'none',
                borderRadius: 14,
                background: selected ? '#f6f6f7' : 'transparent',
                cursor: 'pointer',
                fontSize: 16,
                fontWeight: 500,
                color: '#1a1a1a',
                textAlign: 'left',
                fontFamily: 'inherit',
              }}
            >
              <Flag code={opt.flagCode} emoji={opt.flag} />
              {opt.label}
            </button>
          );
        })}
      </div>,
      document.body,
    );

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Language"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 14px',
          border: 'none',
          borderRadius: 999,
          background: '#fff',
          boxShadow:
            '0 1px 2px rgba(16,24,40,.06), 0 0 0 1px rgba(16,24,40,.08)',
          cursor: 'pointer',
          fontSize: 15,
          fontWeight: 600,
          color: '#1a1a1a',
          lineHeight: 1.2,
          fontFamily: 'inherit',
        }}
      >
        <Flag code={current.flagCode} emoji={current.flag} />
        {current.label}
      </button>
      {menu}
    </div>
  );
}
